import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Banknote, LayoutList, ListChecks, Users } from 'lucide-react'
import { StatusBadge } from '@/components/hr/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/payroll/')({
  component: PayrollDashboard,
})

function PayrollDashboard() {
  const navigate = useNavigate()
  const { data: runsData, isLoading: runsLoading } = useQuery(
    orpc.hr.listPayrollRuns.queryOptions({ input: { page: 1, limit: 1 } }),
  )

  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery(
    orpc.hr.listSalaryAssignments.queryOptions({ input: {} }),
  )

  const latestRun = (runsData?.data ?? [])[0] as
    | {
        id: number
        periodStart: string
        periodEnd: string
        status: string
        totalGross?: string | null
        totalDeductions?: string | null
        totalNet?: string | null
        runAt?: string | null
        employeeCount?: number | null
      }
    | undefined

  const assignments = (assignmentsData ?? []) as unknown as { assignment: { employeeId: number } }[]
  const uniqueEmployees = new Set(assignments.map((a) => a.assignment.employeeId)).size

  const fmt = (val?: string | null) =>
    val ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'

  const quickLinks = [
    {
      title: 'Components',
      description: 'Earnings & deduction components',
      href: '/hr/payroll/components',
      icon: LayoutList,
    },
    {
      title: 'Structures',
      description: 'Salary structure templates',
      href: '/hr/payroll/structures',
      icon: ListChecks,
    },
    {
      title: 'Payroll Runs',
      description: 'Run & review payroll periods',
      href: '/hr/payroll/runs',
      icon: Banknote,
    },
  ]

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Payroll</h1>
        <p className="text-muted-foreground">Manage salary structures, components, and payroll runs</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Employees with Salary</CardTitle>
          </CardHeader>
          <CardContent>
            {assignmentsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{uniqueEmployees}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Latest Run Net Payroll</CardTitle>
          </CardHeader>
          <CardContent>
            {runsLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{fmt(latestRun?.totalNet)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Latest Run Status</CardTitle>
          </CardHeader>
          <CardContent>
            {runsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : latestRun ? (
              <StatusBadge status={latestRun.status} />
            ) : (
              <span className="text-muted-foreground text-sm">No runs yet</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Latest payroll run card */}
      {latestRun && (
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Latest Payroll Run</CardTitle>
                <CardDescription>
                  {new Date(latestRun.periodStart).toLocaleDateString()} —{' '}
                  {new Date(latestRun.periodEnd).toLocaleDateString()}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void navigate({ to: `/hr/payroll/runs/${latestRun.id}` })}
              >
                View Details
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Gross</p>
                <p className="text-lg font-semibold">{fmt(latestRun.totalGross)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Deductions</p>
                <p className="text-lg font-semibold">{fmt(latestRun.totalDeductions)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net</p>
                <p className="text-lg font-semibold">{fmt(latestRun.totalNet)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickLinks.map((item) => (
          <Link key={item.title} to={item.href}>
          <Card
            className="hover:shadow-md transition-shadow cursor-pointer"
          >
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <div className="p-2 rounded-lg bg-muted">
                <item.icon className="h-5 w-5" />
              </div>
              <CardTitle className="text-lg">{item.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{item.description}</CardDescription>
            </CardContent>
          </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
